export interface ProviderCapability {
  id: string;
  label: string;
  description?: string;
  group: 'data' | 'search' | 'actions';
  modifies: boolean;
  tools: string[];
  requiredScopes?: string[];
  /** Used by the platform itself (GitLab's CASFS backups), never by agents: deliberately no tools. */
  platformOnly?: true;
}

export interface ProviderAuthOAuth2 {
  type: 'oauth2';
  authorizationUrl: string;
  tokenUrl: string;
  envClientId: string;
  envClientSecret: string;
  scopes: string[];
  supportsPkce?: boolean;
  supportsIncrementalScopes?: boolean;
}

export interface ProviderAuthApiKey {
  type: 'api_key';
  headerName: string;
  headerValuePrefix?: string;
  requiresInstanceUrl?: boolean;
  testEndpoint?: string;
  envClientId?: string;
  envClientSecret?: string;
}

export interface ProviderAuthLoginWidget {
  type: 'login_widget';
  envBotToken: string;
}

/**
 * "Bring your own app" — the user creates their own platform-side application
 * (Discord App, BotFather bot, custom Reddit App, etc.) and pastes the
 * credentials into our connect form. We never share a deploy-wide token,
 * so each tenant gets their own rate-limit pool, their own bot identity,
 * and their own webhook URL.
 *
 * Discord uses this via the Developer Portal (App ID, Public Key, Bot Token,
 * Client ID, Client Secret). Telegram uses this via @BotFather (single Bot
 * Token, validated via getMe).
 */
export interface ProviderAuthBringYourOwnAppField {
  /** Field key submitted in the POST body to /api/auth/connect/{provider}. */
  key:
    | 'applicationId'
    | 'publicKey'
    | 'botToken'
    | 'clientId'
    | 'clientSecret'
    | 'handle'
    | 'appPassword'
    | 'pdsUrl';
  label: string;
  /** Render hint for the form input. password fields are obscured by default. */
  inputType: 'text' | 'password' | 'url';
  placeholder?: string;
  helpText?: string;
  required: boolean;
}

export interface ProviderAuthBringYourOwnApp {
  type: 'byo_app';
  /** Form fields the user must supply when connecting. */
  fields: ProviderAuthBringYourOwnAppField[];
  /**
   * Optional URL template the user must configure on the platform side as
   * their webhook. `{applicationId}` / `{webhookSecret}` are filled in by
   * the connect route and surfaced back to the UI on success.
   */
  webhookUrlTemplate?: string;
  /** Doc page that walks the user through creating the platform-side app. */
  docsUrl?: string;
  /** Short instructions shown above the form. */
  setupHint?: string;
}

export type ProviderAuth =
  | ProviderAuthOAuth2
  | ProviderAuthApiKey
  | ProviderAuthLoginWidget
  | ProviderAuthBringYourOwnApp;

export interface ProviderMcpConfig {
  type: 'native' | 'hosted';
  package?: string;
  serverUrl?: string;
}

export interface ProviderConfig {
  id: string;
  name: string;
  simpleIconSlug: string;
  category: 'social' | 'music' | 'creative' | 'events' | 'commerce' | 'developer' | 'productivity' | 'communication' | 'automation' | 'health' | 'medical' | 'finance' | 'travel' | 'hosting' | 'ai';
  auth: ProviderAuth;
  capabilities: ProviderCapability[];
  mcp: ProviderMcpConfig;
}

export function isProviderAvailable(provider: ProviderConfig): boolean {
  if (provider.auth.type === 'api_key') return true;
  if (provider.auth.type === 'byo_app') return true; // user supplies credentials
  if (provider.auth.type === 'login_widget') {
    return !!process.env[(provider.auth as ProviderAuthLoginWidget).envBotToken];
  }
  const auth = provider.auth as ProviderAuthOAuth2;
  return !!(process.env[auth.envClientId] && process.env[auth.envClientSecret]);
}

export function getAvailableProviderIds(): string[] {
  return Object.values(PROVIDERS).filter(isProviderAvailable).map(p => p.id);
}

export const PROVIDERS: Record<string, ProviderConfig> = {
  google: {
    id: 'google',
    name: 'Google',
    simpleIconSlug: 'google',
    category: 'productivity',
    auth: {
      type: 'oauth2',
      authorizationUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
      tokenUrl: 'https://oauth2.googleapis.com/token',
      envClientId: 'GOOGLE_CLIENT_ID',
      envClientSecret: 'GOOGLE_CLIENT_SECRET',
      scopes: ['https://www.googleapis.com/auth/userinfo.email', 'https://www.googleapis.com/auth/userinfo.profile'],
      supportsIncrementalScopes: true,
    },
    capabilities: [
      { id: 'account_data', label: 'Access your profile and contacts', group: 'data', modifies: false, tools: ['google_get_profile', 'google_get_contacts'], requiredScopes: ['https://www.googleapis.com/auth/userinfo.profile'] },
      { id: 'gmail_read', label: 'Read your emails and threads', group: 'data', modifies: false, tools: ['gmail_list_messages', 'gmail_get_message', 'gmail_list_threads', 'gmail_search'], requiredScopes: ['https://www.googleapis.com/auth/gmail.readonly'] },
      { id: 'drive_read', label: 'Browse your files and documents', group: 'data', modifies: false, tools: ['drive_list_files', 'drive_get_file', 'drive_search'], requiredScopes: ['https://www.googleapis.com/auth/drive.readonly'] },
      { id: 'calendar_read', label: 'View your calendar events and schedule', group: 'data', modifies: false, tools: ['calendar_list_events', 'calendar_get_event', 'calendar_list_calendars'], requiredScopes: ['https://www.googleapis.com/auth/calendar.readonly'] },
      { id: 'youtube_read', label: 'See your YouTube subscriptions and watch history', group: 'data', modifies: false, tools: ['youtube_list_subscriptions', 'youtube_list_playlists', 'youtube_get_channel'], requiredScopes: ['https://www.googleapis.com/auth/youtube.readonly'] },
      { id: 'gmail_send', label: 'Send emails and create drafts', group: 'actions', modifies: true, tools: ['gmail_send', 'gmail_create_draft'], requiredScopes: ['https://www.googleapis.com/auth/gmail.send'] },
      { id: 'drive_write', label: 'Upload and edit files', group: 'actions', modifies: true, tools: ['drive_upload', 'drive_update', 'drive_create_folder'], requiredScopes: ['https://www.googleapis.com/auth/drive.file'] },
      { id: 'calendar_write', label: 'Create and edit calendar events', group: 'actions', modifies: true, tools: ['calendar_create_event', 'calendar_update_event', 'calendar_delete_event'], requiredScopes: ['https://www.googleapis.com/auth/calendar.events'] },
      { id: 'youtube_write', label: 'Upload videos', group: 'actions', modifies: true, tools: ['youtube_upload_video'], requiredScopes: ['https://www.googleapis.com/auth/youtube.upload'] },
    ],
    mcp: { type: 'native', package: '@anthropic/google-workspace-mcp' },
  },

  'google-chat': {
    id: 'google-chat',
    name: 'Google Chat',
    simpleIconSlug: 'googlechat',
    category: 'communication',
    auth: {
      type: 'oauth2',
      authorizationUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
      tokenUrl: 'https://oauth2.googleapis.com/token',
      envClientId: 'GOOGLE_CLIENT_ID',
      envClientSecret: 'GOOGLE_CLIENT_SECRET',
      scopes: [
        'https://www.googleapis.com/auth/chat.bot',
        'https://www.googleapis.com/auth/chat.spaces.readonly',
      ],
      supportsPkce: true,
    },
    capabilities: [
      { id: 'send_messages', label: 'Send messages and cards in Chat spaces', group: 'actions', modifies: true, tools: ['google_chat_send_message', 'google_chat_send_card'] },
      { id: 'list_spaces', label: 'List spaces and rooms the bot is in', group: 'data', modifies: false, tools: ['google_chat_list_spaces'] },
    ],
    mcp: { type: 'hosted' },
  },

  x: {
    id: 'x',
    name: 'X / Twitter',
    simpleIconSlug: 'x',
    category: 'social',
    auth: {
      type: 'oauth2',
      authorizationUrl: 'https://x.com/i/oauth2/authorize',
      tokenUrl: 'https://api.x.com/2/oauth2/token',
      envClientId: 'X_CLIENT_ID',
      envClientSecret: 'X_CLIENT_SECRET',
      scopes: ['users.read', 'users.email', 'tweet.read', 'follows.read', 'like.read', 'offline.access'],
      supportsPkce: true,
    },
    capabilities: [
      { id: 'account_data', label: 'Access your profile, tweets, and followers', group: 'data', modifies: false, tools: ['x_get_profile', 'x_get_tweets', 'x_get_followers', 'x_get_following', 'x_get_likes', 'x_get_bookmarks', 'x_get_lists'] },
      { id: 'search', label: 'Search tweets and users', group: 'search', modifies: false, tools: ['x_search_tweets', 'x_search_users'] },
      { id: 'post', label: 'Create tweets, like, retweet, and bookmark', group: 'actions', modifies: true, tools: ['x_create_tweet', 'x_like', 'x_retweet', 'x_bookmark', 'x_follow', 'x_send_dm'], requiredScopes: ['tweet.write', 'like.write', 'follows.write', 'bookmark.write'] },
      { id: 'send_messages', label: 'Send X DMs (bridged conversations)', group: 'actions', modifies: true, tools: ['x_send_dm', 'x_send_dm_in_conversation'], requiredScopes: ['dm.read', 'dm.write'] },
      { id: 'publish_posts', label: 'Publish tweets and threads (approval-gated)', group: 'actions', modifies: true, tools: ['x_post_tweet', 'x_post_thread', 'x_delete_tweet'], requiredScopes: ['tweet.write'] },
    ],
    mcp: { type: 'hosted' },
  },

  spotify: {
    id: 'spotify',
    name: 'Spotify',
    simpleIconSlug: 'spotify',
    category: 'music',
    auth: {
      type: 'oauth2',
      authorizationUrl: 'https://accounts.spotify.com/authorize',
      tokenUrl: 'https://accounts.spotify.com/api/token',
      envClientId: 'SPOTIFY_CLIENT_ID',
      envClientSecret: 'SPOTIFY_CLIENT_SECRET',
      scopes: ['user-read-private', 'user-library-read', 'user-read-currently-playing', 'user-read-recently-played', 'user-read-playback-state', 'user-top-read'],
    },
    capabilities: [
      { id: 'account_data', label: 'Access your profile, playlists, and saved music', group: 'data', modifies: false, tools: ['spotify_get_profile', 'spotify_get_playlists', 'spotify_get_saved_tracks', 'spotify_get_saved_albums', 'spotify_get_playlist_tracks', 'spotify_get_top_tracks'], requiredScopes: ['user-read-private', 'user-library-read'] },
      { id: 'listening_activity', label: "See your listening history and what's playing now", group: 'data', modifies: false, tools: ['spotify_get_now_playing', 'spotify_get_recently_played', 'spotify_get_queue'], requiredScopes: ['user-read-currently-playing', 'user-read-recently-played', 'user-read-playback-state'] },
      { id: 'search', label: 'Search for songs, albums, and artists', group: 'search', modifies: false, tools: ['spotify_search', 'spotify_get_recommendations'] },
      { id: 'manage_playlists', label: 'Create and edit playlists', group: 'actions', modifies: true, tools: ['spotify_create_playlist', 'spotify_add_to_playlist', 'spotify_remove_from_playlist', 'spotify_reorder_playlist'], requiredScopes: ['playlist-modify-public', 'playlist-modify-private'] },
      { id: 'playback', label: 'Control playback (play, pause, skip)', group: 'actions', modifies: true, tools: ['spotify_play', 'spotify_pause', 'spotify_skip', 'spotify_set_volume', 'spotify_add_to_queue'], requiredScopes: ['user-modify-playback-state'] },
    ],
    mcp: { type: 'hosted' },
  },

  discord: {
    id: 'discord',
    name: 'Discord',
    simpleIconSlug: 'discord',
    category: 'communication',
    // Per-user/per-agent: each owner brings their own Discord App from the
    // Developer Portal. We never use a shared DISCORD_BOT_TOKEN env var —
    // sharing one would pool rate limits across tenants and force every
    // agent to share an identity. The per-app webhook URL embeds the App ID
    // so signature verification can look up the right public key.
    auth: {
      type: 'byo_app',
      setupHint: 'Create an Application at https://discord.com/developers/applications, then paste the IDs and secrets below. After connecting, set the App\'s "Interactions Endpoint URL" to the URL we generate for you.',
      fields: [
        { key: 'applicationId', label: 'Application ID', inputType: 'text', required: true, placeholder: '1234567890123456789', helpText: 'General Information → Application ID' },
        { key: 'publicKey', label: 'Public Key', inputType: 'text', required: true, placeholder: '64-char hex', helpText: 'General Information → Public Key (Ed25519)' },
        { key: 'botToken', label: 'Bot Token', inputType: 'password', required: true, placeholder: 'MTIzNDU2Nzg5...', helpText: 'Bot → Token (click Reset Token if you don\'t have it)' },
        { key: 'clientId', label: 'Client ID', inputType: 'text', required: false, placeholder: 'usually same as Application ID', helpText: 'OAuth2 → Client ID' },
        { key: 'clientSecret', label: 'Client Secret', inputType: 'password', required: false, placeholder: 'optional, only needed for OAuth installs', helpText: 'OAuth2 → Client Secret' },
      ],
      webhookUrlTemplate: '/api/webhooks/discord/{applicationId}/interactions',
      docsUrl: 'https://discord.com/developers/docs/interactions/overview',
    },
    capabilities: [
      { id: 'inbound_messages', label: 'Receive Discord DMs and slash-command interactions (HTTP-only mode)', group: 'data', modifies: false, tools: ['discord_get_recent_conversations'] },
      { id: 'send_messages', label: 'Send Discord DMs and reply to interactions', group: 'actions', modifies: true, tools: ['discord_send_dm', 'discord_reply_to_interaction'] },
      { id: 'webhook_post', label: 'Post to one channel with a Discord webhook', group: 'actions', modifies: true, tools: ['discord_send_webhook'] },
      { id: 'register_commands', label: 'Register slash commands for your installed guilds', group: 'actions', modifies: true, tools: ['discord_register_slash_command'] },
      { id: 'post_in_channels', label: 'Post in guild channels (approval-gated)', group: 'actions', modifies: true, tools: ['discord_send_in_channel'] },
    ],
    mcp: { type: 'hosted' },
  },

  // ─── Meta messaging products (single app, three providers) ────────────────
  whatsapp: {
    id: 'whatsapp',
    name: 'WhatsApp Business',
    simpleIconSlug: 'whatsapp',
    category: 'communication',
    auth: {
      type: 'oauth2',
      authorizationUrl: 'https://www.facebook.com/v22.0/dialog/oauth',
      tokenUrl: 'https://graph.facebook.com/v22.0/oauth/access_token',
      envClientId: 'META_APP_ID',
      envClientSecret: 'META_APP_SECRET',
      scopes: ['whatsapp_business_messaging', 'whatsapp_business_management', 'business_management'],
    },
    capabilities: [
      { id: 'inbound_messages', label: 'Receive inbound WhatsApp messages and statuses', group: 'data', modifies: false, tools: ['whatsapp_get_recent_conversations'] },
      { id: 'send_messages', label: 'Send WhatsApp messages and templates on your behalf', group: 'actions', modifies: true, tools: ['whatsapp_send_text', 'whatsapp_send_template'] },
      { id: 'manage_templates', label: 'List and manage WhatsApp message templates', group: 'data', modifies: false, tools: ['whatsapp_list_templates'] },
    ],
    mcp: { type: 'hosted' },
  },

  messenger: {
    id: 'messenger',
    name: 'Facebook Messenger',
    simpleIconSlug: 'facebook',
    category: 'social',
    auth: {
      type: 'oauth2',
      authorizationUrl: 'https://www.facebook.com/v22.0/dialog/oauth',
      tokenUrl: 'https://graph.facebook.com/v22.0/oauth/access_token',
      envClientId: 'META_APP_ID',
      envClientSecret: 'META_APP_SECRET',
      scopes: ['pages_show_list', 'pages_messaging', 'pages_manage_metadata', 'pages_read_engagement', 'pages_read_user_content', 'pages_manage_posts'],
    },
    capabilities: [
      { id: 'inbound_messages', label: 'Receive inbound Messenger messages and postbacks', group: 'data', modifies: false, tools: ['messenger_get_recent_conversations'] },
      { id: 'send_messages', label: 'Send Messenger replies on your behalf (24h window-gated)', group: 'actions', modifies: true, tools: ['messenger_send_text', 'messenger_send_with_human_agent_tag'] },
      { id: 'read_page_posts', label: 'Read posts on your connected Pages', group: 'data', modifies: false, tools: ['messenger_get_page_posts'] },
      { id: 'publish_page_posts', label: 'Publish posts to your Pages (approval-gated)', group: 'actions', modifies: true, tools: ['messenger_create_post'] },
    ],
    mcp: { type: 'hosted' },
  },

  instagram: {
    id: 'instagram',
    name: 'Instagram (Business / Creator)',
    simpleIconSlug: 'instagram',
    category: 'social',
    auth: {
      type: 'oauth2',
      authorizationUrl: 'https://api.instagram.com/oauth/authorize',
      tokenUrl: 'https://api.instagram.com/oauth/access_token',
      envClientId: 'META_APP_ID',
      envClientSecret: 'META_APP_SECRET',
      scopes: ['instagram_business_basic', 'instagram_business_manage_messages', 'instagram_business_manage_comments', 'instagram_business_content_publish'],
    },
    capabilities: [
      { id: 'inbound_messages', label: 'Receive inbound Instagram DMs', group: 'data', modifies: false, tools: ['instagram_get_recent_dms'] },
      { id: 'send_messages', label: 'Send Instagram DMs (24h window-gated)', group: 'actions', modifies: true, tools: ['instagram_send_dm'] },
      { id: 'manage_comments', label: 'Reply to comments on your posts', group: 'actions', modifies: true, tools: ['instagram_reply_to_comment'] },
      { id: 'manage_mentions', label: 'See and act on @mentions of your account', group: 'data', modifies: false, tools: ['instagram_get_recent_mentions'] },
      { id: 'publish_content', label: 'Publish posts and reels (approval-gated)', group: 'actions', modifies: true, tools: ['instagram_publish_post', 'instagram_publish_reel', 'instagram_get_media'] },
    ],
    mcp: { type: 'hosted' },
  },

  tiktok: {
    id: 'tiktok',
    name: 'TikTok',
    simpleIconSlug: 'tiktok',
    category: 'social',
    auth: {
      type: 'oauth2',
      authorizationUrl: 'https://www.tiktok.com/v2/auth/authorize/',
      tokenUrl: 'https://open.tiktokapis.com/v2/oauth/token/',
      envClientId: 'TIKTOK_CLIENT_KEY',
      envClientSecret: 'TIKTOK_CLIENT_SECRET',
      scopes: ['user.info.basic', 'video.list', 'video.upload', 'video.publish'],
    },
    capabilities: [
      // TikTokSkill (webagents/typescript/src/skills/messaging/tiktok) is
      // PUBLISH-ONLY in v1 — only video + photo carousel publishing is
      // implemented. Read-side tools (profile/videos/followers/analytics)
      // and search are tracked in roadmap but NOT wired to a host skill,
      // so they are intentionally absent here. Adding them back without
      // an implementation produces "tool not found" at invocation time.
      { id: 'publish_posts', label: 'Publish videos and photo carousels (approval-gated)', group: 'actions', modifies: true, tools: ['tiktok_publish_video', 'tiktok_publish_photo'] },
    ],
    mcp: { type: 'hosted' },
  },

  soundcloud: {
    id: 'soundcloud',
    name: 'SoundCloud',
    simpleIconSlug: 'soundcloud',
    category: 'music',
    auth: {
      type: 'oauth2',
      authorizationUrl: 'https://api.soundcloud.com/connect',
      tokenUrl: 'https://api.soundcloud.com/oauth2/token',
      envClientId: 'SOUNDCLOUD_CLIENT_ID',
      envClientSecret: 'SOUNDCLOUD_CLIENT_SECRET',
      scopes: ['non-expiring'],
      supportsPkce: true,
    },
    capabilities: [
      { id: 'account_data', label: 'Access your tracks, playlists, and followers', group: 'data', modifies: false, tools: ['soundcloud_get_profile', 'soundcloud_get_tracks', 'soundcloud_get_playlists', 'soundcloud_get_followers', 'soundcloud_get_following', 'soundcloud_get_likes', 'soundcloud_get_reposts'] },
      { id: 'search', label: 'Search tracks and users', group: 'search', modifies: false, tools: ['soundcloud_search_tracks', 'soundcloud_search_users'] },
      { id: 'posting', label: 'Upload tracks and create playlists', group: 'actions', modifies: true, tools: ['soundcloud_upload_track', 'soundcloud_create_playlist', 'soundcloud_like', 'soundcloud_repost'] },
    ],
    mcp: { type: 'hosted' },
  },

  deviantart: {
    id: 'deviantart',
    name: 'DeviantArt',
    simpleIconSlug: 'deviantart',
    category: 'creative',
    auth: {
      type: 'oauth2',
      authorizationUrl: 'https://www.deviantart.com/oauth2/authorize',
      tokenUrl: 'https://www.deviantart.com/oauth2/token',
      envClientId: 'DEVIANTART_CLIENT_ID',
      envClientSecret: 'DEVIANTART_CLIENT_SECRET',
      scopes: ['browse', 'user', 'collection', 'gallery'],
    },
    capabilities: [
      { id: 'account_data', label: 'Access your gallery, collections, and watchers', group: 'data', modifies: false, tools: ['deviantart_get_profile', 'deviantart_get_gallery', 'deviantart_get_deviations', 'deviantart_get_collections', 'deviantart_get_watchers', 'deviantart_get_watching', 'deviantart_get_stats'] },
      { id: 'search', label: 'Search deviations and browse topics', group: 'search', modifies: false, tools: ['deviantart_search_deviations', 'deviantart_browse_topics'] },
      { id: 'posting', label: 'Submit deviations and manage collections', group: 'actions', modifies: true, tools: ['deviantart_submit_deviation', 'deviantart_add_to_collection'] },
    ],
    mcp: { type: 'hosted' },
  },

  eventbrite: {
    id: 'eventbrite',
    name: 'Eventbrite',
    simpleIconSlug: 'eventbrite',
    category: 'events',
    auth: {
      type: 'oauth2',
      authorizationUrl: 'https://www.eventbrite.com/oauth/authorize',
      tokenUrl: 'https://www.eventbrite.com/oauth/token',
      envClientId: 'EVENTBRITE_CLIENT_ID',
      envClientSecret: 'EVENTBRITE_CLIENT_SECRET',
      scopes: [],
    },
    capabilities: [
      { id: 'account_data', label: 'Access your events, attendees, and orders', group: 'data', modifies: false, tools: ['eventbrite_get_user', 'eventbrite_get_events', 'eventbrite_get_attendees', 'eventbrite_get_orders', 'eventbrite_get_ticket_classes', 'eventbrite_get_venues'] },
      { id: 'search', label: 'Search events', group: 'search', modifies: false, tools: ['eventbrite_search_events'] },
      { id: 'manage', label: 'Create and update events', group: 'actions', modifies: true, tools: ['eventbrite_create_event', 'eventbrite_update_event', 'eventbrite_create_ticket_class'] },
    ],
    mcp: { type: 'hosted' },
  },

  etsy: {
    id: 'etsy',
    name: 'Etsy',
    simpleIconSlug: 'etsy',
    category: 'commerce',
    auth: {
      type: 'oauth2',
      authorizationUrl: 'https://www.etsy.com/oauth/connect',
      tokenUrl: 'https://api.etsy.com/v3/public/oauth/token',
      envClientId: 'ETSY_CLIENT_ID',
      envClientSecret: 'ETSY_CLIENT_SECRET',
      scopes: ['shops_r', 'listings_r', 'transactions_r'],
      supportsPkce: true,
    },
    capabilities: [
      { id: 'account_data', label: 'Access your shop, listings, and orders', group: 'data', modifies: false, tools: ['etsy_get_shop', 'etsy_get_listings', 'etsy_get_orders', 'etsy_get_reviews', 'etsy_get_shop_receipts', 'etsy_get_favorites'] },
      { id: 'search', label: 'Search listings', group: 'search', modifies: false, tools: ['etsy_search_listings'] },
      { id: 'manage', label: 'Create and update listings', group: 'actions', modifies: true, tools: ['etsy_create_listing', 'etsy_update_listing', 'etsy_update_inventory'], requiredScopes: ['listings_w'] },
    ],
    mcp: { type: 'hosted' },
  },

  shopify: {
    id: 'shopify',
    name: 'Shopify',
    simpleIconSlug: 'shopify',
    category: 'commerce',
    auth: {
      type: 'oauth2',
      authorizationUrl: 'https://{shop}.myshopify.com/admin/oauth/authorize',
      tokenUrl: 'https://{shop}.myshopify.com/admin/oauth/access_token',
      envClientId: 'SHOPIFY_CLIENT_ID',
      envClientSecret: 'SHOPIFY_CLIENT_SECRET',
      scopes: ['read_products', 'read_orders', 'read_customers', 'read_analytics'],
    },
    capabilities: [
      { id: 'account_data', label: 'Access your store, products, and orders', group: 'data', modifies: false, tools: ['shopify_get_store_info', 'shopify_get_products', 'shopify_get_inventory', 'shopify_get_orders', 'shopify_get_customers', 'shopify_get_analytics', 'shopify_get_collections'] },
      { id: 'search', label: 'Search products and orders', group: 'search', modifies: false, tools: ['shopify_search_products', 'shopify_search_orders'] },
      { id: 'manage', label: 'Create and update products, manage orders', group: 'actions', modifies: true, tools: ['shopify_create_product', 'shopify_update_product', 'shopify_fulfill_order', 'shopify_update_inventory'], requiredScopes: ['write_products', 'write_orders'] },
    ],
    mcp: { type: 'native', package: '@anthropic/shopify-mcp' },
  },

  meetup: {
    id: 'meetup',
    name: 'Meetup',
    simpleIconSlug: 'meetup',
    category: 'events',
    auth: {
      type: 'oauth2',
      authorizationUrl: 'https://secure.meetup.com/oauth2/authorize',
      tokenUrl: 'https://secure.meetup.com/oauth2/access',
      envClientId: 'MEETUP_CLIENT_ID',
      envClientSecret: 'MEETUP_CLIENT_SECRET',
      scopes: ['basic', 'event_management'],
    },
    capabilities: [
      { id: 'account_data', label: 'Access your groups, events, and RSVPs', group: 'data', modifies: false, tools: ['meetup_get_profile', 'meetup_get_groups', 'meetup_get_events', 'meetup_get_rsvps', 'meetup_get_members'] },
      { id: 'search', label: 'Search events and groups', group: 'search', modifies: false, tools: ['meetup_search_events', 'meetup_search_groups'] },
      { id: 'manage', label: 'Create events and RSVP', group: 'actions', modifies: true, tools: ['meetup_create_event', 'meetup_rsvp', 'meetup_create_group'] },
    ],
    mcp: { type: 'hosted' },
  },

  telegram: {
    id: 'telegram',
    name: 'Telegram',
    simpleIconSlug: 'telegram',
    category: 'communication',
    // Per-user/per-agent: each owner creates their own bot via @BotFather and
    // pastes the token into our connect form. The connect route mints a
    // per-bot webhook secret and registers a unique URL with Telegram so the
    // bot's identity, rate limits, and revocation are scoped to that owner.
    auth: {
      type: 'byo_app',
      setupHint: 'Open @BotFather on Telegram, run /newbot (or /token for an existing one), and paste the API token below.',
      fields: [
        { key: 'botToken', label: 'Bot Token', inputType: 'password', required: true, placeholder: '1234567890:AAEhBP0av28...', helpText: 'Format: <bot_id>:<35-char-secret>' },
      ],
      webhookUrlTemplate: '/api/webhooks/telegram/{webhookSecret}',
      docsUrl: 'https://core.telegram.org/bots#how-do-i-create-a-bot',
    },
    capabilities: [
      { id: 'inbound_messages', label: 'Receive Telegram DMs to your bot', group: 'data', modifies: false, tools: ['telegram_get_recent_conversations'] },
      { id: 'send_messages', label: 'Send Telegram messages, photos, and documents', group: 'actions', modifies: true, tools: ['telegram_send_text', 'telegram_send_photo', 'telegram_send_document', 'telegram_set_typing'] },
      { id: 'manage_groups', label: 'Receive and respond to Telegram group messages (Phase 1.5)', group: 'actions', modifies: true, tools: [] },
    ],
    mcp: { type: 'hosted' },
  },

  ifttt: {
    id: 'ifttt',
    name: 'IFTTT',
    simpleIconSlug: 'ifttt',
    category: 'automation',
    auth: {
      type: 'oauth2',
      authorizationUrl: 'https://ifttt.com/authorize',
      tokenUrl: 'https://ifttt.com/oauth/token',
      envClientId: 'IFTTT_CLIENT_ID',
      envClientSecret: 'IFTTT_CLIENT_SECRET',
      scopes: [],
    },
    capabilities: [
      { id: 'account_data', label: 'Access your applets and activity log', group: 'data', modifies: false, tools: ['ifttt_get_applets', 'ifttt_get_services', 'ifttt_get_activity_log'] },
      { id: 'manage', label: 'Trigger, enable, and disable applets', group: 'actions', modifies: true, tools: ['ifttt_trigger_applet', 'ifttt_enable_applet', 'ifttt_disable_applet'] },
    ],
    mcp: { type: 'hosted' },
  },

  reddit: {
    id: 'reddit',
    name: 'Reddit',
    simpleIconSlug: 'reddit',
    category: 'social',
    auth: {
      type: 'oauth2',
      authorizationUrl: 'https://www.reddit.com/api/v1/authorize',
      tokenUrl: 'https://www.reddit.com/api/v1/access_token',
      envClientId: 'REDDIT_CLIENT_ID',
      envClientSecret: 'REDDIT_CLIENT_SECRET',
      scopes: ['identity', 'read', 'submit', 'edit'],
    },
    capabilities: [
      { id: 'read_profile', label: 'Read your Reddit profile and karma', group: 'data', modifies: false, tools: ['reddit_get_profile'] },
      { id: 'publish_content', label: 'Create Reddit posts (approval-gated)', group: 'actions', modifies: true, tools: ['reddit_create_post'], requiredScopes: ['submit'] },
      { id: 'comment', label: 'Reply with comments (approval-gated)', group: 'actions', modifies: true, tools: ['reddit_create_comment'], requiredScopes: ['submit'] },
    ],
    mcp: { type: 'hosted' },
  },

  linkedin: {
    id: 'linkedin',
    name: 'LinkedIn',
    simpleIconSlug: 'linkedin',
    category: 'social',
    auth: {
      type: 'oauth2',
      authorizationUrl: 'https://www.linkedin.com/oauth/v2/authorization',
      tokenUrl: 'https://www.linkedin.com/oauth/v2/accessToken',
      envClientId: 'LINKEDIN_CLIENT_ID',
      envClientSecret: 'LINKEDIN_CLIENT_SECRET',
      scopes: ['openid', 'profile', 'email', 'w_member_social'],
    },
    capabilities: [
      { id: 'read_profile', label: 'Read your LinkedIn profile (publish-only integration)', group: 'data', modifies: false, tools: ['linkedin_get_profile'] },
      { id: 'read_recent_posts', label: 'Read your recent LinkedIn posts', group: 'data', modifies: false, tools: ['linkedin_get_recent_posts'] },
      { id: 'publish_content', label: 'Create LinkedIn posts on your profile (approval-gated)', group: 'actions', modifies: true, tools: ['linkedin_create_post'], requiredScopes: ['w_member_social'] },
    ],
    mcp: { type: 'hosted' },
  },

  pinterest: {
    id: 'pinterest',
    name: 'Pinterest',
    simpleIconSlug: 'pinterest',
    category: 'creative',
    auth: {
      type: 'oauth2',
      authorizationUrl: 'https://api.pinterest.com/oauth/',
      tokenUrl: 'https://api.pinterest.com/v5/oauth/token',
      envClientId: 'PINTEREST_APP_ID',
      envClientSecret: 'PINTEREST_APP_SECRET',
      scopes: ['boards:read', 'pins:read', 'user_accounts:read'],
    },
    capabilities: [
      { id: 'account_data', label: 'Access your pins, boards, and saved content', group: 'data', modifies: false, tools: ['pinterest_get_profile', 'pinterest_get_boards', 'pinterest_get_pins', 'pinterest_get_saved_pins', 'pinterest_get_followers'] },
      { id: 'search', label: 'Search pins', group: 'search', modifies: false, tools: ['pinterest_search_pins'] },
      { id: 'posting', label: 'Create pins and boards', group: 'actions', modifies: true, tools: ['pinterest_create_pin', 'pinterest_create_board', 'pinterest_save_pin'], requiredScopes: ['boards:write', 'pins:write'] },
    ],
    mcp: { type: 'hosted' },
  },

  twitch: {
    id: 'twitch',
    name: 'Twitch',
    simpleIconSlug: 'twitch',
    category: 'social',
    auth: {
      type: 'oauth2',
      authorizationUrl: 'https://id.twitch.tv/oauth2/authorize',
      tokenUrl: 'https://id.twitch.tv/oauth2/token',
      envClientId: 'TWITCH_CLIENT_ID',
      envClientSecret: 'TWITCH_CLIENT_SECRET',
      scopes: ['user:read:email', 'channel:read:subscriptions'],
    },
    capabilities: [
      { id: 'account_data', label: 'Access your channel, followers, and streams', group: 'data', modifies: false, tools: ['twitch_get_channel', 'twitch_get_followers', 'twitch_get_subscribers', 'twitch_get_streams', 'twitch_get_vods', 'twitch_get_clips', 'twitch_get_chat_settings'] },
      { id: 'search', label: 'Search channels and categories', group: 'search', modifies: false, tools: ['twitch_search_channels', 'twitch_search_categories'] },
      { id: 'streaming', label: 'Send chat messages and create clips', group: 'actions', modifies: true, tools: ['twitch_send_chat_message', 'twitch_create_clip', 'twitch_update_channel'] },
    ],
    mcp: { type: 'hosted' },
  },

  github: {
    id: 'github',
    name: 'GitHub',
    simpleIconSlug: 'github',
    category: 'developer',
    auth: {
      type: 'oauth2',
      authorizationUrl: 'https://github.com/login/oauth/authorize',
      tokenUrl: 'https://github.com/login/oauth/access_token',
      envClientId: 'GITHUB_CONNECT_CLIENT_ID',
      envClientSecret: 'GITHUB_CONNECT_CLIENT_SECRET',
      scopes: ['read:user', 'repo', 'notifications'],
    },
    capabilities: [
      { id: 'account_data', label: 'Access your profile, repos, and starred projects', group: 'data', modifies: false, tools: ['github_get_profile', 'github_get_repos', 'github_get_starred', 'github_get_notifications', 'github_get_issues', 'github_get_pull_requests', 'github_get_discussions'] },
      { id: 'code_review', label: 'Create issues, comments, and reviews', group: 'actions', modifies: true, tools: ['github_create_issue', 'github_create_comment', 'github_create_review'] },
      { id: 'code_push', label: 'Push code and create pull requests', group: 'actions', modifies: true, tools: ['github_push_code', 'github_create_pr'] },
      { id: 'repo_admin', label: 'Manage repository settings', group: 'actions', modifies: true, tools: ['github_manage_repo_settings'] },
    ],
    mcp: { type: 'native', package: '@anthropic/github-mcp' },
  },

  // GitLab, for CASFS project backups only (ADR 0063 section 15): the narrowest scope that writes one
  // project's repository. No connector tools ride on it (capability lists no tools), so connecting it
  // gives agents nothing. gitlab.com only; a self-managed host is CASFS_GITLAB_HOST in git-backup.ts and
  // needs its own OAuth application (not wired: authorizationUrl is static here).
  gitlab: {
    id: 'gitlab',
    name: 'GitLab',
    simpleIconSlug: 'gitlab',
    category: 'developer',
    auth: {
      type: 'oauth2',
      authorizationUrl: 'https://gitlab.com/oauth/authorize',
      tokenUrl: 'https://gitlab.com/oauth/token',
      envClientId: 'GITLAB_CONNECT_CLIENT_ID',
      envClientSecret: 'GITLAB_CONNECT_CLIENT_SECRET',
      scopes: ['read_user', 'write_repository'],
      supportsPkce: true,
    },
    capabilities: [
      { id: 'project_backups', label: 'Back up your projects to a GitLab repository', group: 'actions', modifies: true, tools: [], platformOnly: true },
    ],
    mcp: { type: 'native' },
  },

  notion: {
    id: 'notion',
    name: 'Notion',
    simpleIconSlug: 'notion',
    category: 'productivity',
    auth: {
      type: 'oauth2',
      authorizationUrl: 'https://api.notion.com/v1/oauth/authorize',
      tokenUrl: 'https://api.notion.com/v1/oauth/token',
      envClientId: 'NOTION_CLIENT_ID',
      envClientSecret: 'NOTION_CLIENT_SECRET',
      scopes: [],
      supportsPkce: true,
    },
    capabilities: [
      { id: 'account_data', label: 'Access your pages, databases, and workspace', group: 'data', modifies: false, tools: ['notion_get_pages', 'notion_get_databases', 'notion_get_workspace', 'notion_get_page_content', 'notion_query_database'] },
      { id: 'search', label: 'Search pages and databases', group: 'search', modifies: false, tools: ['notion_search'] },
      { id: 'editing', label: 'Create and edit pages and databases', group: 'actions', modifies: true, tools: ['notion_create_page', 'notion_update_page', 'notion_create_database', 'notion_update_database'] },
    ],
    mcp: { type: 'native', package: '@anthropic/notion-mcp' },
  },

  slack: {
    id: 'slack',
    name: 'Slack',
    simpleIconSlug: 'slack',
    category: 'communication',
    auth: {
      type: 'oauth2',
      authorizationUrl: 'https://slack.com/oauth/v2/authorize',
      tokenUrl: 'https://slack.com/api/oauth.v2.access',
      envClientId: 'SLACK_CLIENT_ID',
      envClientSecret: 'SLACK_CLIENT_SECRET',
      scopes: ['app_mentions:read', 'chat:write', 'im:history', 'im:read', 'im:write', 'users:read', 'files:write'],
    },
    capabilities: [
      { id: 'inbound_messages', label: 'Receive Slack DMs and @-mentions in installed workspaces', group: 'data', modifies: false, tools: ['slack_get_recent_conversations'] },
      { id: 'send_messages', label: 'Send Slack DMs', group: 'actions', modifies: true, tools: ['slack_send_dm', 'slack_reply_in_thread'] },
      { id: 'post_in_channels', label: 'Post in Slack channels (approval-gated)', group: 'actions', modifies: true, tools: ['slack_post_in_channel'] },
      { id: 'upload_files', label: 'Upload files to Slack', group: 'actions', modifies: true, tools: ['slack_upload_file'] },
      { id: 'manage_channels', label: 'Create and manage channels (Phase 1.5)', group: 'actions', modifies: true, tools: [] },
    ],
    mcp: { type: 'hosted' },
  },

  uber: {
    id: 'uber',
    name: 'Uber',
    simpleIconSlug: 'uber',
    category: 'travel',
    auth: {
      type: 'oauth2',
      authorizationUrl: 'https://login.uber.com/oauth/v2/authorize',
      tokenUrl: 'https://login.uber.com/oauth/v2/token',
      envClientId: 'UBER_CLIENT_ID',
      envClientSecret: 'UBER_CLIENT_SECRET',
      scopes: ['profile', 'history'],
    },
    capabilities: [
      { id: 'account_data', label: 'Access your trip history and profile', group: 'data', modifies: false, tools: ['uber_get_profile', 'uber_get_trip_history', 'uber_get_payment_methods', 'uber_get_places'] },
      { id: 'estimate', label: 'Get ride estimates', group: 'search', modifies: false, tools: ['uber_get_ride_estimate'] },
      { id: 'ride', label: 'Request rides', group: 'actions', modifies: true, tools: ['uber_request_ride'] },
    ],
    mcp: { type: 'hosted' },
  },

  stripe: {
    id: 'stripe',
    name: 'Stripe',
    simpleIconSlug: 'stripe',
    category: 'finance',
    auth: {
      type: 'oauth2',
      authorizationUrl: 'https://connect.stripe.com/oauth/authorize',
      tokenUrl: 'https://connect.stripe.com/oauth/token',
      envClientId: 'STRIPE_CONNECT_CLIENT_ID',
      envClientSecret: 'STRIPE_CONNECT_CLIENT_SECRET',
      scopes: ['read_only'],
    },
    capabilities: [
      { id: 'account_data', label: 'Access your customers, payments, and subscriptions', group: 'data', modifies: false, tools: ['stripe_get_customers', 'stripe_get_payments', 'stripe_get_subscriptions', 'stripe_get_invoices', 'stripe_get_products', 'stripe_get_balance', 'stripe_get_payouts'] },
      { id: 'search', label: 'Search customers and payments', group: 'search', modifies: false, tools: ['stripe_search_customers', 'stripe_search_payments'] },
      { id: 'manage', label: 'Create customers, payment links, and invoices', group: 'actions', modifies: true, tools: ['stripe_create_customer', 'stripe_create_payment_link', 'stripe_create_invoice', 'stripe_issue_refund'] },
    ],
    mcp: { type: 'native', package: '@anthropic/stripe-mcp' },
  },

  dropbox: {
    id: 'dropbox',
    name: 'Dropbox',
    simpleIconSlug: 'dropbox',
    category: 'productivity',
    auth: {
      type: 'oauth2',
      authorizationUrl: 'https://www.dropbox.com/oauth2/authorize',
      tokenUrl: 'https://api.dropboxapi.com/oauth2/token',
      envClientId: 'DROPBOX_CLIENT_ID',
      envClientSecret: 'DROPBOX_CLIENT_SECRET',
      scopes: ['files.metadata.read', 'files.content.read', 'sharing.read', 'account_info.read'],
      supportsPkce: true,
    },
    capabilities: [
      { id: 'account_data', label: 'Access your files, folders, and sharing info', group: 'data', modifies: false, tools: ['dropbox_get_account', 'dropbox_list_files', 'dropbox_get_file', 'dropbox_get_sharing', 'dropbox_get_activity'] },
      { id: 'search', label: 'Search files', group: 'search', modifies: false, tools: ['dropbox_search'] },
      { id: 'manage', label: 'Upload and manage files', group: 'actions', modifies: true, tools: ['dropbox_upload', 'dropbox_move', 'dropbox_create_folder', 'dropbox_share'], requiredScopes: ['files.content.write', 'sharing.write'] },
    ],
    mcp: { type: 'native', package: '@anthropic/dropbox-mcp' },
  },

  figma: {
    id: 'figma',
    name: 'Figma',
    simpleIconSlug: 'figma',
    category: 'creative',
    auth: {
      type: 'oauth2',
      authorizationUrl: 'https://www.figma.com/oauth',
      tokenUrl: 'https://api.figma.com/v1/oauth/token',
      envClientId: 'FIGMA_CLIENT_ID',
      envClientSecret: 'FIGMA_CLIENT_SECRET',
      scopes: ['files:read'],
    },
    capabilities: [
      { id: 'account_data', label: 'Access your design files and team projects', group: 'data', modifies: false, tools: ['figma_get_files', 'figma_get_file_details', 'figma_get_components', 'figma_get_team_projects', 'figma_get_comments'] },
      { id: 'search', label: 'Search files and components', group: 'search', modifies: false, tools: ['figma_search'] },
      { id: 'commenting', label: 'Add comments to designs', group: 'actions', modifies: true, tools: ['figma_create_comment'], requiredScopes: ['file_comments:write'] },
    ],
    mcp: { type: 'native', package: '@anthropic/figma-mcp' },
  },

  asana: {
    id: 'asana',
    name: 'Asana',
    simpleIconSlug: 'asana',
    category: 'productivity',
    auth: {
      type: 'oauth2',
      authorizationUrl: 'https://app.asana.com/-/oauth_authorize',
      tokenUrl: 'https://app.asana.com/-/oauth_token',
      envClientId: 'ASANA_CLIENT_ID',
      envClientSecret: 'ASANA_CLIENT_SECRET',
      scopes: ['default'],
    },
    capabilities: [
      { id: 'account_data', label: 'Access your tasks, projects, and workspaces', group: 'data', modifies: false, tools: ['asana_get_tasks', 'asana_get_projects', 'asana_get_workspaces', 'asana_get_assigned_work'] },
      { id: 'search', label: 'Search tasks', group: 'search', modifies: false, tools: ['asana_search_tasks'] },
      { id: 'manage', label: 'Create and update tasks', group: 'actions', modifies: true, tools: ['asana_create_task', 'asana_update_task', 'asana_create_project'] },
    ],
    mcp: { type: 'native', package: '@anthropic/asana-mcp' },
  },

  bluesky: {
    id: 'bluesky',
    name: 'Bluesky',
    simpleIconSlug: 'bluesky',
    category: 'social',
    // Bluesky's official OAuth (Atproto OAuth2) is still alpha-status and has
    // substantial PDS-discovery + DPoP requirements. App passwords are the
    // supported third-party path today: each owner generates an App Password
    // in https://bsky.app/settings/app-passwords (revocable per-Robutler).
    auth: {
      type: 'byo_app',
      setupHint: 'Generate an App Password at https://bsky.app/settings/app-passwords (do NOT use your main account password — it would be invalid for third-party apps with 2FA).',
      fields: [
        { key: 'handle', label: 'Bluesky Handle', inputType: 'text', required: true, placeholder: 'alice.bsky.social', helpText: 'Your handle, without the leading @.' },
        { key: 'appPassword', label: 'App Password', inputType: 'password', required: true, placeholder: 'xxxx-xxxx-xxxx-xxxx', helpText: 'Generated at bsky.app → Settings → App Passwords' },
        { key: 'pdsUrl', label: 'PDS URL', inputType: 'url', required: false, placeholder: 'https://bsky.social', helpText: 'Only needed if you self-host your PDS.' },
      ],
      docsUrl: 'https://docs.bsky.app/docs/advanced-guides/app-passwords',
    },
    capabilities: [
      { id: 'read_profile', label: 'Read your Bluesky profile', group: 'data', modifies: false, tools: ['bluesky_get_profile'] },
      { id: 'read_timeline', label: 'Read your Bluesky timeline', group: 'data', modifies: false, tools: ['bluesky_get_timeline'] },
      { id: 'publish_content', label: 'Create Bluesky posts (approval-gated)', group: 'actions', modifies: true, tools: ['bluesky_create_post'] },
    ],
    mcp: { type: 'hosted' },
  },

  patreon: {
    id: 'patreon',
    name: 'Patreon',
    simpleIconSlug: 'patreon',
    category: 'creative',
    auth: {
      type: 'oauth2',
      authorizationUrl: 'https://www.patreon.com/oauth2/authorize',
      tokenUrl: 'https://www.patreon.com/api/oauth2/token',
      envClientId: 'PATREON_CLIENT_ID',
      envClientSecret: 'PATREON_CLIENT_SECRET',
      scopes: ['identity', 'campaigns', 'campaigns.members'],
    },
    capabilities: [
      { id: 'account_data', label: 'Access your campaigns, members, and earnings', group: 'data', modifies: false, tools: ['patreon_get_campaign', 'patreon_get_members', 'patreon_get_pledges', 'patreon_get_posts', 'patreon_get_earnings', 'patreon_get_tiers'] },
      { id: 'posting', label: 'Create and update posts', group: 'actions', modifies: true, tools: ['patreon_create_post', 'patreon_update_post'] },
    ],
    mcp: { type: 'hosted' },
  },

  calendly: {
    id: 'calendly',
    name: 'Calendly',
    simpleIconSlug: 'calendly',
    category: 'productivity',
    auth: {
      type: 'oauth2',
      authorizationUrl: 'https://auth.calendly.com/oauth/authorize',
      tokenUrl: 'https://auth.calendly.com/oauth/token',
      envClientId: 'CALENDLY_CLIENT_ID',
      envClientSecret: 'CALENDLY_CLIENT_SECRET',
      scopes: [],
    },
    capabilities: [
      { id: 'account_data', label: 'Access your scheduled events and availability', group: 'data', modifies: false, tools: ['calendly_get_user', 'calendly_get_event_types', 'calendly_get_scheduled_events', 'calendly_get_invitees', 'calendly_get_availability'] },
      { id: 'manage', label: 'Create scheduling links and cancel events', group: 'actions', modifies: true, tools: ['calendly_create_scheduling_link', 'calendly_cancel_event'] },
    ],
    mcp: { type: 'hosted' },
  },

  zoom: {
    id: 'zoom',
    name: 'Zoom',
    simpleIconSlug: 'zoom',
    category: 'communication',
    auth: {
      type: 'oauth2',
      authorizationUrl: 'https://zoom.us/oauth/authorize',
      tokenUrl: 'https://zoom.us/oauth/token',
      envClientId: 'ZOOM_CLIENT_ID',
      envClientSecret: 'ZOOM_CLIENT_SECRET',
      scopes: ['meeting:read', 'user:read', 'recording:read'],
    },
    capabilities: [
      { id: 'account_data', label: 'Access your meetings, recordings, and contacts', group: 'data', modifies: false, tools: ['zoom_get_profile', 'zoom_get_meetings', 'zoom_get_recordings', 'zoom_get_contacts', 'zoom_get_meeting_history'] },
      { id: 'manage', label: 'Create and manage meetings', group: 'actions', modifies: true, tools: ['zoom_create_meeting', 'zoom_update_meeting', 'zoom_delete_meeting'], requiredScopes: ['meeting:write'] },
    ],
    mcp: { type: 'hosted' },
  },

  microsoft: {
    id: 'microsoft',
    name: 'Microsoft / Office 365',
    simpleIconSlug: 'microsoft',
    category: 'productivity',
    auth: {
      type: 'oauth2',
      authorizationUrl: 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize',
      tokenUrl: 'https://login.microsoftonline.com/common/oauth2/v2.0/token',
      envClientId: 'MICROSOFT_CLIENT_ID',
      envClientSecret: 'MICROSOFT_CLIENT_SECRET',
      scopes: ['openid', 'profile', 'email', 'User.Read', 'Mail.Read', 'Calendars.Read', 'Files.Read'],
      supportsPkce: true,
    },
    capabilities: [
      { id: 'account_data', label: 'Access your emails, files, and calendar', group: 'data', modifies: false, tools: ['microsoft_get_profile', 'microsoft_get_emails', 'microsoft_get_calendar_events', 'microsoft_get_files', 'microsoft_get_contacts', 'microsoft_get_recent_documents'] },
      { id: 'search', label: 'Search emails and files', group: 'search', modifies: false, tools: ['microsoft_search_emails', 'microsoft_search_files'] },
      { id: 'manage', label: 'Send emails, create events, and upload files', group: 'actions', modifies: true, tools: ['microsoft_send_email', 'microsoft_create_event', 'microsoft_upload_file', 'microsoft_create_document'], requiredScopes: ['Mail.Send', 'Calendars.ReadWrite', 'Files.ReadWrite'] },
    ],
    mcp: { type: 'native', package: '@anthropic/microsoft-mcp' },
  },

  ticketmaster: {
    id: 'ticketmaster',
    name: 'Ticketmaster',
    simpleIconSlug: 'ticketmaster',
    category: 'events',
    auth: {
      type: 'oauth2',
      authorizationUrl: 'https://auth.ticketmaster.com/as/authorization.oauth2',
      tokenUrl: 'https://auth.ticketmaster.com/as/token.oauth2',
      envClientId: 'TICKETMASTER_CONSUMER_KEY',
      envClientSecret: 'TICKETMASTER_CONSUMER_SECRET',
      scopes: [],
    },
    capabilities: [
      { id: 'account_data', label: 'Access your ticket purchases and favorites', group: 'data', modifies: false, tools: ['ticketmaster_get_profile', 'ticketmaster_get_orders', 'ticketmaster_get_ticket_history', 'ticketmaster_get_favorites'] },
      { id: 'search', label: 'Search events, concerts, and shows', group: 'search', modifies: false, tools: ['ticketmaster_search_events', 'ticketmaster_get_event_details', 'ticketmaster_get_venues', 'ticketmaster_get_attractions', 'ticketmaster_get_nearby_events'] },
    ],
    mcp: { type: 'hosted' },
  },

  supabase: {
    id: 'supabase',
    name: 'Supabase',
    simpleIconSlug: 'supabase',
    category: 'developer',
    auth: {
      type: 'oauth2',
      authorizationUrl: 'https://api.supabase.com/v1/oauth/authorize',
      tokenUrl: 'https://api.supabase.com/v1/oauth/token',
      envClientId: 'SUPABASE_CLIENT_ID',
      envClientSecret: 'SUPABASE_CLIENT_SECRET',
      scopes: [],
      supportsPkce: true,
    },
    capabilities: [
      { id: 'account_data', label: 'Access your projects, databases, and functions', group: 'data', modifies: false, tools: ['supabase_get_projects', 'supabase_get_project_details', 'supabase_get_tables', 'supabase_query_database', 'supabase_get_functions', 'supabase_get_storage_buckets'] },
      { id: 'manage', label: 'Create tables, insert data, and invoke functions', group: 'actions', modifies: true, tools: ['supabase_create_table', 'supabase_insert_rows', 'supabase_update_rows', 'supabase_invoke_function'] },
    ],
    mcp: { type: 'hosted' },
  },

  fitbit: {
    id: 'fitbit',
    name: 'Fitbit',
    simpleIconSlug: 'fitbit',
    category: 'health',
    auth: {
      type: 'oauth2',
      authorizationUrl: 'https://www.fitbit.com/oauth2/authorize',
      tokenUrl: 'https://api.fitbit.com/oauth2/token',
      envClientId: 'FITBIT_CLIENT_ID',
      envClientSecret: 'FITBIT_CLIENT_SECRET',
      scopes: ['activity', 'heartrate', 'nutrition', 'profile', 'sleep', 'weight'],
      supportsPkce: true,
    },
    capabilities: [
      { id: 'account_data', label: 'Access your activity, sleep, and heart rate data', group: 'data', modifies: false, tools: ['fitbit_get_profile', 'fitbit_get_activity_summary', 'fitbit_get_daily_steps', 'fitbit_get_sleep_log', 'fitbit_get_heart_rate', 'fitbit_get_workouts', 'fitbit_get_nutrition_log', 'fitbit_get_weight_log', 'fitbit_get_body_composition', 'fitbit_get_badges', 'fitbit_get_devices'] },
      { id: 'logging', label: 'Log food, water, weight, and activities', group: 'actions', modifies: true, tools: ['fitbit_log_food', 'fitbit_log_water', 'fitbit_log_weight', 'fitbit_log_activity'] },
    ],
    mcp: { type: 'hosted' },
  },

  withings: {
    id: 'withings',
    name: 'Withings',
    simpleIconSlug: 'withings',
    category: 'health',
    auth: {
      type: 'oauth2',
      authorizationUrl: 'https://account.withings.com/oauth2_user/authorize2',
      tokenUrl: 'https://wbsapi.withings.net/v2/oauth2',
      envClientId: 'WITHINGS_CLIENT_ID',
      envClientSecret: 'WITHINGS_CLIENT_SECRET',
      scopes: ['user.metrics', 'user.activity', 'user.sleepevents'],
    },
    capabilities: [
      { id: 'account_data', label: 'Access your measurements, sleep, and activity data', group: 'data', modifies: false, tools: ['withings_get_user', 'withings_get_measurements', 'withings_get_sleep_summary', 'withings_get_sleep_details', 'withings_get_activity', 'withings_get_heart_data', 'withings_get_devices', 'withings_get_goals'] },
      { id: 'manage', label: 'Set health goals', group: 'actions', modifies: true, tools: ['withings_set_goal'] },
    ],
    mcp: { type: 'hosted' },
  },

  oura: {
    id: 'oura',
    name: 'Oura Ring',
    simpleIconSlug: 'oura',
    category: 'health',
    auth: {
      type: 'oauth2',
      authorizationUrl: 'https://cloud.ouraring.com/oauth/authorize',
      tokenUrl: 'https://api.ouraring.com/oauth/token',
      envClientId: 'OURA_CLIENT_ID',
      envClientSecret: 'OURA_CLIENT_SECRET',
      scopes: ['daily', 'heartrate', 'personal', 'session', 'spo2', 'tag', 'workout'],
    },
    capabilities: [
      { id: 'account_data', label: 'Access your sleep, readiness, and activity data', group: 'data', modifies: false, tools: ['oura_get_profile', 'oura_get_daily_sleep', 'oura_get_sleep_periods', 'oura_get_daily_readiness', 'oura_get_daily_activity', 'oura_get_heart_rate', 'oura_get_spo2', 'oura_get_daily_stress', 'oura_get_sessions', 'oura_get_tags', 'oura_get_workouts'] },
    ],
    mcp: { type: 'hosted' },
  },

  whoop: {
    id: 'whoop',
    name: 'WHOOP',
    simpleIconSlug: 'whoop',
    category: 'health',
    auth: {
      type: 'oauth2',
      authorizationUrl: 'https://api.prod.whoop.com/oauth/oauth2/auth',
      tokenUrl: 'https://api.prod.whoop.com/oauth/oauth2/token',
      envClientId: 'WHOOP_CLIENT_ID',
      envClientSecret: 'WHOOP_CLIENT_SECRET',
      scopes: ['read:recovery', 'read:cycles', 'read:sleep', 'read:workout', 'read:profile', 'read:body_measurement'],
    },
    capabilities: [
      { id: 'account_data', label: 'Access your recovery, strain, and sleep data', group: 'data', modifies: false, tools: ['whoop_get_profile', 'whoop_get_recovery', 'whoop_get_sleep', 'whoop_get_strain', 'whoop_get_workouts', 'whoop_get_cycles', 'whoop_get_heart_rate', 'whoop_get_body_measurements'] },
    ],
    mcp: { type: 'hosted' },
  },

  epic: {
    id: 'epic',
    name: 'Epic / MyChart',
    simpleIconSlug: 'epic',
    category: 'medical',
    auth: {
      type: 'oauth2',
      authorizationUrl: 'https://fhir.epic.com/interconnect-fhir-oauth/oauth2/authorize',
      tokenUrl: 'https://fhir.epic.com/interconnect-fhir-oauth/oauth2/token',
      envClientId: 'EPIC_CLIENT_ID',
      envClientSecret: 'EPIC_CLIENT_SECRET',
      scopes: ['openid', 'fhirUser', 'patient/*.read', 'launch/patient'],
      supportsPkce: true,
    },
    capabilities: [
      { id: 'account_data', label: 'Access your patient records, medications, and appointments', group: 'data', modifies: false, tools: ['epic_get_patient', 'epic_get_conditions', 'epic_get_medications', 'epic_get_allergies', 'epic_get_immunizations', 'epic_get_lab_results', 'epic_get_vital_signs', 'epic_get_appointments', 'epic_get_care_plans', 'epic_get_documents', 'epic_get_procedures'] },
      { id: 'search', label: 'Search observations', group: 'search', modifies: false, tools: ['epic_search_observations'] },
      { id: 'scheduling', label: 'Schedule appointments', group: 'actions', modifies: true, tools: ['epic_schedule_appointment'] },
    ],
    mcp: { type: 'native', package: 'fhir-mcp-server' },
  },

  // API Key providers
  luma: {
    id: 'luma',
    name: 'Luma',
    simpleIconSlug: 'luma',
    category: 'events',
    auth: {
      type: 'api_key',
      headerName: 'x-luma-api-key',
      testEndpoint: 'https://api.lu.ma/public/v1/user/get-self',
    },
    capabilities: [
      { id: 'account_data', label: 'Access your events, guest lists, and registrations', group: 'data', modifies: false, tools: ['luma_get_events', 'luma_get_guests', 'luma_get_registrations', 'luma_get_calendar'] },
      { id: 'manage', label: 'Create events and manage guests', group: 'actions', modifies: true, tools: ['luma_create_event', 'luma_add_guests', 'luma_update_event'] },
    ],
    mcp: { type: 'hosted' },
  },

  n8n: {
    id: 'n8n',
    name: 'n8n',
    simpleIconSlug: 'n8n',
    category: 'automation',
    auth: {
      type: 'api_key',
      headerName: 'X-N8N-API-KEY',
      requiresInstanceUrl: true,
      testEndpoint: '/api/v1/workflows',
    },
    capabilities: [
      { id: 'account_data', label: 'Access your workflows and executions', group: 'data', modifies: false, tools: ['n8n_get_workflows', 'n8n_get_workflow_details', 'n8n_get_executions', 'n8n_get_execution_data', 'n8n_get_credentials_list'] },
      { id: 'manage', label: 'Execute and manage workflows', group: 'actions', modifies: true, tools: ['n8n_execute_workflow', 'n8n_activate_workflow', 'n8n_deactivate_workflow'] },
    ],
    mcp: { type: 'hosted' },
  },

  linktree: {
    id: 'linktree',
    name: 'Linktree',
    simpleIconSlug: 'linktree',
    category: 'social',
    auth: {
      type: 'oauth2',
      authorizationUrl: 'https://linktr.ee/oauth/authorize',
      tokenUrl: 'https://linktr.ee/oauth/token',
      envClientId: 'LINKTREE_CLIENT_ID',
      envClientSecret: 'LINKTREE_CLIENT_SECRET',
      scopes: ['profile:read', 'links:read'],
    },
    capabilities: [
      { id: 'account_data', label: 'Access your profile and links', group: 'data', modifies: false, tools: ['linktree_get_profile', 'linktree_get_links'] },
      { id: 'manage_links', label: 'Create and edit links', group: 'actions', modifies: true, tools: ['linktree_create_link', 'linktree_update_link', 'linktree_delete_link'], requiredScopes: ['links:write'] },
    ],
    mcp: { type: 'hosted' },
  },

  vercel: {
    id: 'vercel',
    name: 'Vercel',
    simpleIconSlug: 'vercel',
    category: 'hosting',
    auth: {
      type: 'oauth2',
      authorizationUrl: 'https://vercel.com/integrations/new',
      tokenUrl: 'https://api.vercel.com/v2/oauth/access_token',
      envClientId: 'VERCEL_CLIENT_ID',
      envClientSecret: 'VERCEL_CLIENT_SECRET',
      scopes: ['read', 'write'],
    },
    capabilities: [
      { id: 'account_data', label: 'Access your projects and deployments', group: 'data', modifies: false, tools: ['vercel_list_projects', 'vercel_get_project', 'vercel_list_deployments', 'vercel_get_deployment'] },
      { id: 'deploy', label: 'Create and manage deployments', group: 'actions', modifies: true, tools: ['vercel_create_project', 'vercel_deploy', 'vercel_delete_deployment', 'vercel_set_env_vars'] },
      { id: 'domains', label: 'Manage custom domains', group: 'actions', modifies: true, tools: ['vercel_add_domain', 'vercel_remove_domain', 'vercel_list_domains'] },
    ],
    mcp: { type: 'hosted' },
  },

  cloudflare: {
    id: 'cloudflare',
    name: 'Cloudflare',
    simpleIconSlug: 'cloudflare',
    category: 'hosting',
    auth: {
      type: 'oauth2',
      authorizationUrl: 'https://dash.cloudflare.com/oauth2/authorize',
      tokenUrl: 'https://dash.cloudflare.com/oauth2/token',
      envClientId: 'CLOUDFLARE_CLIENT_ID',
      envClientSecret: 'CLOUDFLARE_CLIENT_SECRET',
      scopes: ['account:read', 'zone:read', 'zone:edit', 'pages:write'],
    },
    capabilities: [
      { id: 'account_data', label: 'Access your zones and Pages projects', group: 'data', modifies: false, tools: ['cloudflare_list_zones', 'cloudflare_list_pages_projects', 'cloudflare_get_pages_project', 'cloudflare_list_pages_deployments'] },
      { id: 'deploy', label: 'Deploy to Cloudflare Pages', group: 'actions', modifies: true, tools: ['cloudflare_create_pages_project', 'cloudflare_deploy_pages', 'cloudflare_delete_pages_deployment'] },
      { id: 'domains', label: 'Manage DNS and custom domains', group: 'actions', modifies: true, tools: ['cloudflare_add_dns_record', 'cloudflare_update_dns_record', 'cloudflare_delete_dns_record', 'cloudflare_set_pages_domain'] },
    ],
    mcp: { type: 'hosted' },
  },

  twilio: {
    id: 'twilio',
    name: 'Twilio',
    simpleIconSlug: 'twilio',
    category: 'communication',
    auth: {
      type: 'api_key',
      headerName: 'Authorization',
      envClientId: 'TWILIO_ACCOUNT_SID',
      envClientSecret: 'TWILIO_AUTH_TOKEN',
    },
    capabilities: [
      { id: 'inbound_messages', label: 'Receive inbound SMS/MMS to your imported phone numbers', group: 'data', modifies: false, tools: ['twilio_get_recent_conversations'] },
      { id: 'send_messages', label: 'Send SMS and MMS', group: 'actions', modifies: true, tools: ['twilio_send_sms', 'twilio_send_mms'] },
      { id: 'lookup_number', label: 'Look up phone number metadata before sending', group: 'data', modifies: false, tools: ['twilio_lookup_number'] },
      { id: 'voice', label: 'Make and manage phone calls (Phase 1.5)', group: 'actions', modifies: true, tools: [] },
      { id: 'verify', label: 'Send verification codes (Phase 1.5)', group: 'actions', modifies: true, tools: [] },
    ],
    mcp: { type: 'hosted' },
  },

  sendgrid: {
    id: 'sendgrid',
    name: 'SendGrid',
    simpleIconSlug: 'sendgrid',
    category: 'communication',
    auth: {
      type: 'api_key',
      headerName: 'Authorization',
      envClientId: 'SENDGRID_API_KEY',
    },
    capabilities: [
      { id: 'account_data', label: 'Access sender identities and email stats', group: 'data', modifies: false, tools: ['sendgrid_list_senders', 'sendgrid_get_stats', 'sendgrid_list_templates', 'sendgrid_get_template'] },
      { id: 'send_messages', label: 'Send transactional and marketing emails', group: 'actions', modifies: true, tools: ['sendgrid_send_email', 'sendgrid_send_template', 'sendgrid_send_context_email'] },
      { id: 'contacts', label: 'Manage contact lists and segments', group: 'actions', modifies: true, tools: ['sendgrid_add_contacts', 'sendgrid_list_contacts', 'sendgrid_create_list', 'sendgrid_delete_contacts'] },
      { id: 'templates', label: 'Create and manage email templates', group: 'actions', modifies: true, tools: ['sendgrid_create_template', 'sendgrid_update_template', 'sendgrid_delete_template'] },
    ],
    mcp: { type: 'hosted' },
  },

  daytona: {
    id: 'daytona',
    name: 'Daytona',
    simpleIconSlug: 'daytona',
    category: 'developer',
    auth: {
      type: 'api_key',
      headerName: 'Authorization',
      envClientId: 'DAYTONA_API_KEY',
    },
    capabilities: [
      { id: 'account_data', label: 'Access workspaces and sandbox status', group: 'data', modifies: false, tools: ['daytona_list_sandboxes', 'daytona_get_sandbox', 'daytona_list_snapshots'] },
      { id: 'sandboxes', label: 'Create and manage sandboxes', group: 'actions', modifies: true, tools: ['daytona_create_sandbox', 'daytona_start_sandbox', 'daytona_stop_sandbox', 'daytona_delete_sandbox'] },
      { id: 'execute', label: 'Run code and commands in sandboxes', group: 'actions', modifies: true, tools: ['daytona_exec_command', 'daytona_upload_file', 'daytona_download_file', 'daytona_list_files'] },
      { id: 'snapshots', label: 'Create and restore sandbox snapshots', group: 'actions', modifies: true, tools: ['daytona_create_snapshot', 'daytona_restore_snapshot', 'daytona_delete_snapshot'] },
    ],
    mcp: { type: 'hosted' },
  },

  ebay: {
    id: 'ebay',
    name: 'eBay',
    simpleIconSlug: 'ebay',
    category: 'commerce',
    auth: {
      type: 'oauth2',
      authorizationUrl: 'https://auth.ebay.com/oauth2/authorize',
      tokenUrl: 'https://api.ebay.com/identity/v1/oauth2/token',
      envClientId: 'EBAY_CLIENT_ID',
      envClientSecret: 'EBAY_CLIENT_SECRET',
      scopes: ['https://api.ebay.com/oauth/api_scope', 'https://api.ebay.com/oauth/api_scope/sell.inventory', 'https://api.ebay.com/oauth/api_scope/sell.marketing'],
    },
    capabilities: [
      { id: 'account_data', label: 'Access your listings, orders, and seller profile', group: 'data', modifies: false, tools: ['ebay_get_seller_profile', 'ebay_list_items', 'ebay_get_item', 'ebay_list_orders', 'ebay_get_order'] },
      { id: 'search', label: 'Search listings and browse categories', group: 'search', modifies: false, tools: ['ebay_search_items', 'ebay_browse_categories', 'ebay_get_item_details'] },
      { id: 'sell', label: 'Create and manage listings', group: 'actions', modifies: true, tools: ['ebay_create_listing', 'ebay_update_listing', 'ebay_end_listing', 'ebay_revise_price'] },
    ],
    mcp: { type: 'hosted' },
  },

  poshmark: {
    id: 'poshmark',
    name: 'Poshmark',
    simpleIconSlug: 'poshmark',
    category: 'commerce',
    auth: {
      type: 'oauth2',
      authorizationUrl: 'https://poshmark.com/oauth/authorize',
      tokenUrl: 'https://poshmark.com/oauth/token',
      envClientId: 'POSHMARK_CLIENT_ID',
      envClientSecret: 'POSHMARK_CLIENT_SECRET',
      scopes: ['read', 'write'],
    },
    capabilities: [
      { id: 'account_data', label: 'Access your closet, listings, and sales', group: 'data', modifies: false, tools: ['poshmark_get_closet', 'poshmark_list_items', 'poshmark_get_item', 'poshmark_list_sales', 'poshmark_get_earnings'] },
      { id: 'sell', label: 'Create and manage listings', group: 'actions', modifies: true, tools: ['poshmark_create_listing', 'poshmark_update_listing', 'poshmark_delete_listing', 'poshmark_share_listing'] },
    ],
    mcp: { type: 'hosted' },
  },

  mercari: {
    id: 'mercari',
    name: 'Mercari',
    simpleIconSlug: 'mercari',
    category: 'commerce',
    auth: {
      type: 'oauth2',
      authorizationUrl: 'https://www.mercari.com/oauth/authorize',
      tokenUrl: 'https://www.mercari.com/oauth/token',
      envClientId: 'MERCARI_CLIENT_ID',
      envClientSecret: 'MERCARI_CLIENT_SECRET',
      scopes: ['read', 'write'],
    },
    capabilities: [
      { id: 'account_data', label: 'Access your listings, sales, and profile', group: 'data', modifies: false, tools: ['mercari_get_profile', 'mercari_list_items', 'mercari_get_item', 'mercari_list_sales'] },
      { id: 'search', label: 'Search marketplace listings', group: 'search', modifies: false, tools: ['mercari_search_items', 'mercari_browse_categories'] },
      { id: 'sell', label: 'Create and manage listings', group: 'actions', modifies: true, tools: ['mercari_create_listing', 'mercari_update_listing', 'mercari_delete_listing'] },
    ],
    mcp: { type: 'hosted' },
  },

  fal: {
    id: 'fal',
    name: 'fal.ai',
    simpleIconSlug: 'fal',
    category: 'ai',
    auth: {
      type: 'api_key',
      headerName: 'Authorization',
      headerValuePrefix: 'Key',
      testEndpoint: 'https://api.fal.ai/v1/models?limit=1',
    },
    capabilities: [
      { id: 'image_generation', label: 'Generate images from text prompts', group: 'actions', modifies: false, tools: ['generate_image'] },
      { id: 'video_generation', label: 'Generate videos from text or images', group: 'actions', modifies: false, tools: ['generate_video'] },
      { id: 'audio_generation', label: 'Generate audio from text', group: 'actions', modifies: false, tools: ['generate_audio'] },
      { id: '3d_generation', label: 'Generate 3D models from text or images', group: 'actions', modifies: false, tools: ['generate_3d'] },
    ],
    mcp: { type: 'hosted' },
  },

  replicate: {
    id: 'replicate',
    name: 'Replicate',
    simpleIconSlug: 'replicate',
    category: 'ai',
    auth: {
      type: 'api_key',
      headerName: 'Authorization',
      headerValuePrefix: 'Bearer',
      testEndpoint: 'https://api.replicate.com/v1/models',
    },
    capabilities: [
      { id: 'image_generation', label: 'Generate images from text prompts', group: 'actions', modifies: false, tools: ['generate_image'] },
      { id: 'video_generation', label: 'Generate videos from text or images', group: 'actions', modifies: false, tools: ['generate_video'] },
      { id: 'audio_generation', label: 'Generate audio from text', group: 'actions', modifies: false, tools: ['generate_audio'] },
      { id: '3d_generation', label: 'Generate 3D models from text or images', group: 'actions', modifies: false, tools: ['generate_3d'] },
    ],
    mcp: { type: 'hosted' },
  },
};

export function getProvider(id: string): ProviderConfig | undefined {
  return PROVIDERS[id];
}

export function getProvidersByCategory(category: string): ProviderConfig[] {
  return Object.values(PROVIDERS).filter(p => p.category === category);
}

export function getAllCategories(): string[] {
  return [...new Set(Object.values(PROVIDERS).map(p => p.category))];
}

export function getCapabilityTools(provider: ProviderConfig, capabilityIds: string[]): string[] {
  return provider.capabilities
    .filter(c => capabilityIds.includes(c.id))
    .flatMap(c => c.tools);
}

export function getRequiredScopes(provider: ProviderConfig, capabilityIds: string[]): string[] {
  const scopes = new Set<string>();
  if (provider.auth.type === 'oauth2') {
    for (const s of provider.auth.scopes) scopes.add(s);
  }
  for (const cap of provider.capabilities) {
    if (capabilityIds.includes(cap.id) && cap.requiredScopes) {
      for (const s of cap.requiredScopes) scopes.add(s);
    }
  }
  return [...scopes];
}
